import React, { useCallback, useMemo, useState } from "react";
import { Box, DatePicker, Flex, Text } from "orcs-design-system";
import moment from "moment";
import PropTypes from "prop-types";
import { createGlobalStyle } from "styled-components";
import { API_DATE_FORMAT } from "src/consts/global";
import { ACTION_TYPES } from "src/components/OrgChart/context/actionTypes";

const DatePickerContainer = createGlobalStyle`
  .DateRangePicker_picker {
    z-index: 10000 !important; 
  }
  .CalendarDay__hovered_span,
  .CalendarDay__selected_span {
    background: none;
    color: inherit;
    border: 1px solid #e4e7e7;
  }
`;

const isSameDay = (date1, date2) => {
  // if one or both dates are nullish, they are not the same
  if ((!date1 && !date2) || (!date1 && date2) || date1 || !date2) {
    return false;
  }

  // if both dates are valid, compare them
  return date1.isSame(date2, "day");
};

export const DiffDatePicker = ({ state, dispatch }) => {
  const [focusedInput, setFocusedInput] = useState(null);
  const [dates, setDates] = useState({
    fromDate: state?.viewOptions?.fromDate || null,
    toDate: state?.viewOptions?.toDate || null,
  });

  const [today, minDate, maxDate] = useMemo(() => {
    return [
      moment(),
      moment().year(2000).startOf("year"), // what should the min be?
      moment(),
    ];
  }, []);

  const saveDates = useCallback(
    ({ fromDate, toDate }) => {
      const showChanges = fromDate?.isValid() && toDate?.isValid();

      dispatch({
        type: ACTION_TYPES.UPDATE_VIEW_OPTIONS,
        payload: {
          ...state.viewOptions,
          showChanges,
          fromDate: fromDate?.isValid() ? fromDate : null,
          toDate: toDate?.isValid() ? toDate : null,
        },
      });
    },
    [dispatch, state.viewOptions]
  );

  const setDatesSafe = useCallback(
    ({ startDate, endDate }) => {
      // skip if no changes
      if (
        isSameDay(dates.startDate, startDate) &&
        isSameDay(dates.endDate, endDate)
      ) {
        return;
      }

      setDates({
        fromDate: startDate,
        toDate: endDate,
      });

      if ((startDate && endDate) || (!startDate && !endDate)) {
        saveDates({ fromDate: startDate, toDate: endDate });
      }
    },
    [dates.endDate, dates.startDate, saveDates]
  );

  // disable dates outside of the min/max range
  const isOutsideRange = useCallback(
    (date) => {
      return date.isBefore(minDate, "day") || date.isAfter(maxDate, "day");
    },
    [maxDate, minDate]
  );

  // highlight today (perhaps we can use this for something else?)
  const isDayHighlighted = useCallback(
    (date) => {
      return date.isSame(today, "day");
    },
    [today]
  );

  return (
    <Box overflow="hidden">
      <Text>Compare changes</Text>
      <Flex mt="s" flexDirection="row">
        <Box zIndex={100000}>
          <DatePickerContainer />
          <DatePicker
            range
            small
            width="130px"
            withPortal
            keepFocusOnInput
            disableScroll
            showClearDates
            displayFormat={API_DATE_FORMAT}
            minDate={minDate}
            maxDate={maxDate}
            startDate={dates.fromDate}
            endDate={dates.toDate}
            startDatePlaceholderText="From"
            endDatePlaceholderText="To"
            focusedInput={focusedInput}
            onFocusChange={setFocusedInput}
            onDatesChange={setDatesSafe}
            isOutsideRange={isOutsideRange}
            isDayHighlighted={isDayHighlighted}
            numberOfMonths={2}
            minimumNights={1}
            horizontalMargin={0}
          />
        </Box>
      </Flex>
    </Box>
  );
};

DiffDatePicker.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};
